import { ColumnDef } from '@tanstack/react-table';
import { ClockAlert } from 'lucide-react';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import {
  dateOnlyOf,
  formatDateOnly,
  orgCalendarDate,
} from '@/lib/utils/custom-date/custom-date';
import { SurveySubmission } from '../../types/submission.types';

const STATUSES = ['Pending', 'Completed'] as const;

/**
 * A pending survey whose deadline (a date-only value) is before the organization's today: it can no
 * longer be filled out (prototype `expired`).
 */
export const isExpired = (submission: Pick<SurveySubmission, 'Status' | 'Deadline'>, now = new Date()) =>
  submission.Status !== 'Completed' &&
  Boolean(submission.Deadline) &&
  dateOnlyOf(submission.Deadline) < orgCalendarDate(now.toISOString());

interface CreateSubmissionsTableColumnsProps {
  t: (key: string) => string;
  onOpen: (submission: SurveySubmission) => void;
}

/**
 * My responses (HR Module Prototype `PAGES["my-responses"]`): Survey, Deadline (red once expired), Status
 * (Completed, Pending or Expired) and the action: Fill out for an open survey, View otherwise.
 */
export const createSubmissionsTableColumns = ({
  t,
  onOpen,
}: CreateSubmissionsTableColumnsProps): ColumnDef<SurveySubmission>[] => [
  {
    id: 'SurveyTitle',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SURVEY')} />,
    accessorFn: (row) => row.SurveyTitle ?? '-',
    cell: ({ getValue }) => <span className="font-medium">{getValue<string>()}</span>,
  },
  {
    id: 'Deadline',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEADLINE')} />,
    accessorFn: (row) => row.Deadline,
    cell: ({ row }) => (
      <span className={cn('whitespace-nowrap', isExpired(row.original) && 'text-destructive')}>
        {formatDateOnly(row.original.Deadline) || '-'}
      </span>
    ),
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: STATUSES.map((status) => ({ value: status, label: t(status.toUpperCase()) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) =>
      isExpired(row.original) ? (
        <StatusChip tone="error" icon={ClockAlert}>
          {t('EXPIRED')}
        </StatusChip>
      ) : (
        <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
      ),
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const canFill = row.original.Status !== 'Completed' && !isExpired(row.original);
      return (
        <Button
          variant={canFill ? 'default' : 'outline'}
          size="sm"
          onClick={() => onOpen(row.original)}
        >
          {canFill ? t('FILL_OUT') : t('VIEW')}
        </Button>
      );
    },
  },
];
